import {useEffect, useRef, useState} from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {useMapConfig} from '@/hooks/useAppConfig.js'
import {EMISSIVE, INDOOR_STYLE_CONFIG, ROUTE_SLOT, STANDARD_STYLE_URL} from '@/components/map/standardStyle.js'
import personSvg from '@img/person-icon.svg?raw'
import boothSvg from '@img/fiber_manual_record.svg?raw'

/**
 * THE WALK MAP — an indoor camera standing behind someone crossing a terminal.
 *
 * ── ⚠️ WHY THIS IS NOT `RouteMap` (2026-09-23) ────────────────────────────────────
 * The walk ran on `RouteMap`, the shared seam every other map surface in the portal goes
 * through, configured by thirteen props into behaving like this. It worked, and it was slow
 * to open, for a reason that is worth writing down because it is not "the component is big":
 *
 * ⚠️ `RouteMap` LOADS `three` AND BUILDS A 3D CAR THE MOMENT A CAMERA TILTS. The model
 * layer is armed off the map's real pitch — not off whether anyone passed a driver — and
 * this screen opens at 60°. So every walk fetched three.js and the SVG loader over the
 * network, compiled them, and extruded a car nobody could see, because there is no driver on
 * this map at all. That is the load the passenger was waiting through, standing in an
 * arrivals hall on airport wifi.
 *
 * The rest follows from the same mismatch. `RouteMap` carries road snapping, a travelled
 * trail, congestion colouring, leg and stop-over pins, polyline decoding, route framing with
 * padding, a follow camera, a recentre control and a 2D/3D switch — and the walk turns every
 * one of them OFF, several through props that exist only to say "not here" (`holdCamera`,
 * `recentreControl={false}`, `hideOriginPin`). A screen whose configuration is mostly denial
 * is not a caller of that component; it is a different component wearing its props.
 *
 * ⚠️ AND THE COST RAN BOTH WAYS. Every one of those behaviours had to keep working around
 * this one caller — the camera-hold ref exists because the walk's follow camera and the
 * route framing were fighting — so the walk made the shared map harder to change for the
 * five screens that actually want a drive drawn on a street.
 *
 * What is left here is the whole of what the walk needs: an indoor style, a dotted line, two
 * markers, a camera the host aims, and a drag that turns the view instead of moving it.
 *
 * ⚠️ IT DELIBERATELY SHARES `standardStyle.js` WITH `RouteMap`. The style URL, the indoor
 * config, the layer slot and the emissive escape hatch are facts about the basemap, not
 * about this screen — two copies would drift the first time the indoor config is tuned.
 */

/**
 * ⚠️ MAPBOX'S OWN CEILING, AND THE VIEW OPENS AT IT — see `WALK_ZOOM` in `WalkToPickup`.
 * Exported because the caller opens the camera here and must not write a second 22.
 */
export const WALK_MAX_ZOOM = 22

/**
 * How far OUT the walker may pinch. Far enough for the terminal in context, not so far that
 * the floor plan stops being drawn at all.
 */
const WALK_MIN_ZOOM = 12

/**
 * ⚠️ MAPBOX THROWS ON AN INVERTED PAIR — "minZoom must be between -2 and the current
 * maxZoom" — rather than clamping. Dropping the floor to the SDK's own bottom before moving
 * the ceiling means the two are never crossed for even one statement, whichever way either
 * end is travelling.
 */
const MAPBOX_FLOOR = -2

/* Toronto. Only ever on screen for the frame before the first fix aims the camera. */
const DEFAULT_CENTER = [-79.3832, 43.6532]

/* The same mint the drive route is drawn in — one route colour in the portal. */
const ROUTE_COLOR = '#00d4aa'

/**
 * THE MAP THAT OUTLIVES THE SHEET — one per tab, held here rather than in React.
 *
 * A Mapbox map belongs to the element it was built on, so keeping the instance while React
 * took its container away would leave a map rendering into nothing. `host` is therefore a
 * div this module owns: mounting adopts it into the React container, unmounting takes it
 * back out, and the GL context, the style, the downloaded tiles and the camera all stay
 * exactly as the walker left them.
 *
 * ⚠️ DELIBERATELY NOT REACT STATE. State belongs to a component, and every component that
 * could hold this one unmounts when the sheet closes — which is the exact moment the map
 * has to survive. Module scope is the lifetime being asked for: the tab.
 *
 * ⚠️ AND A TAB, NOT A SESSION. A reload starts from nothing, which is correct — that is
 * the point at which a stale camera or an expired token should be thrown away, not nursed.
 *
 * `token` is what the map was built against, so a changed one builds afresh rather than
 * reusing a map authorised by the old.
 */
const cache = {token: null, host: null, map: null}

const ROUTE_SOURCE_ID = 'walk-route'
const ROUTE_LAYER_ID = 'walk-route-line'

export default function WalkMap({
    /** The solved walk as `{lat, lng}` points. */
    pathPoints = null,
    /** The booth at the end of it. */
    destination = null,
    /** The walker. Drawn as the disc; the camera is aimed at them by `focusRequest`. */
    passenger = null,
    /**
     * The camera, as the host wants it — `{id, lat, lng, zoom, pitch, offset, duration}`.
     *
     * ⚠️ `id` IS WHAT MAKES IT FIRE. The walk re-aims on every GPS fix, and several of those
     * ask for a camera barely different from the last; comparing the objects would either
     * re-fly on an identical request or miss a real one. A counter says "this is a new
     * instruction" unambiguously.
     */
    focusRequest = null,
    /** How far the walker may tilt. Mapbox's own ceiling is 60 and it clamps in silence. */
    maxPitch = 85,
    /**
     * WHERE THE MAP IS BUILT — `{lng, lat, zoom, pitch}`, read ONCE on the constructor.
     *
     * ⚠️ WITHOUT IT THE FIRST THING THE MAP DOES IS LOAD THE WRONG PLACE. A Mapbox map is
     * born somewhere, and with no answer that is a default centre at zoom 11 — so the
     * screen fetched and drew a city-wide tile set nobody asked for, and only then cut to
     * a terminal at zoom 22 and fetched that too.
     *
     * The walk knows where it is before the browser is asked for a fix: the arrivals door
     * for this flight, or failing that the booth. Handing that over means the first tiles
     * requested are the ones that end up on screen.
     *
     * ⚠️ AND IT IS NOT LIVE. Later camera work goes through `focusRequest`; this only ever
     * describes the map's birth, so a changed value must never rebuild the map underneath a
     * walker.
     */
    initialCamera = null,
    /** Which floor to open on — arrivals is not the ground floor. */
    defaultFloor = null,
    className = '',
}) {
    /* ⚠️ ONLY THE TOKEN. The config's own `style_url` is the portal-wide basemap, which is
       the one thing this screen must not use — the walk is drawn on Standard with the indoor
       config, and a pale street style has no terminal in it at all. */
    const {token, isError: configFailed} = useMapConfig()
    const containerRef = useRef(null)
    const mapRef = useRef(null)
    const markersRef = useRef([])
    const [ready, setReady] = useState(false)

    /* ── The map, once ────────────────────────────────────────────────────────────
       Keyed on the token alone: the style and its config are constants here, not props, so
       there is nothing else that could justify tearing down a map and losing the camera. */
    useEffect(() => {
        if (!token || !containerRef.current || mapRef.current) return undefined

        /* ⚠️ RE-ADOPTED, NOT REBUILT. `ready` goes straight to true: the source and layer
           added on the first build are still on this map, and so is everything downloaded
           since. See `cache`. */
        if (cache.map && cache.token === token) {
            containerRef.current.appendChild(cache.host)
            mapRef.current = cache.map
            /* The box it comes back into is rarely the one it left — a phone rotated, a
               window resized while it was away. Mapbox measures its container only when
               told to. */
            cache.map.resize()
            setReady(true)
            return () => {
                if (cache.host.parentNode) cache.host.parentNode.removeChild(cache.host)
                mapRef.current = null
                setReady(false)
            }
        }

        mapboxgl.accessToken = token
        /* ⚠️ BUILT ON A DIV THIS MODULE OWNS, not on the React container — see `cache`. */
        const host = document.createElement('div')
        host.style.cssText = 'position:absolute;inset:0'
        containerRef.current.appendChild(host)
        const map = new mapboxgl.Map({
            container: host,
            style: STANDARD_STYLE_URL,
            /* ⚠️ ON THE CONSTRUCTOR, NOWHERE ELSE. Setting the indoor config after `load`
               reproduces the "indoor only appears once you pan" bug exactly — see
               `standardStyle.js`. */
            config: INDOOR_STYLE_CONFIG,
            /* See `initialCamera`. The fallbacks are only ever reached when the walk has no
               idea where it is, which `resolveWalkPlan` makes close to impossible — its
               pickup is never null. */
            center: Number.isFinite(initialCamera?.lat) && Number.isFinite(initialCamera?.lng)
                ? [initialCamera.lng, initialCamera.lat]
                : DEFAULT_CENTER,
            zoom: Number.isFinite(initialCamera?.zoom) ? initialCamera.zoom : 11,
            pitch: Number.isFinite(initialCamera?.pitch) ? initialCamera.pitch : 0,
            maxZoom: WALK_MAX_ZOOM,
            minZoom: WALK_MIN_ZOOM,
            maxPitch,
            attributionControl: false,
        })
        mapRef.current = map

        /* Wired FIRST. A style that fails to load fails quietly — a black map and no
           exception — and on a configured style there is a long list of properties to
           suspect. This is the only thing that will say which. */
        map.on('error', (e) => console.warn('[walk-map]', e?.error || e))

        map.on('load', () => {
            map.setBearing(0)
            map.addSource(ROUTE_SOURCE_ID, {
                type: 'geojson',
                data: {type: 'Feature', geometry: {type: 'LineString', coordinates: []}},
            })
            /**
             * ⚠️ A ZERO-LENGTH DASH WITH A ROUND CAP IS A DOT. `[0, 1.6]` reads as "no
             * dash, then a gap of 1.6 line-widths", and a round cap on a zero-length
             * segment paints a full circle. The units are multiples of the line width, so
             * the spacing scales with the dots rather than needing a pixel value of its own.
             *
             * ⚠️ AND A WALK IS DOTS, NOT A LINE. There is no road in here to draw — a solid
             * stroke across a concourse claims a path that does not exist on the floor.
             *
             * ⚠️ NO CASING. The drive route paints a white casing under its line to hold it
             * against dark tarmac; these dots sit on a pale terminal floor, where a casing
             * is a white halo around every dot and nothing else.
             *
             * `line-emissive-strength` is the lit-style escape hatch — see `EMISSIVE`.
             */
            map.addLayer({
                id: ROUTE_LAYER_ID,
                type: 'line',
                /* ⚠️ NOT OPTIONAL ON STANDARD. Its own layers cannot be reordered, so a
                   layer added with no slot lands wherever Standard decides — in practice
                   under the basemap's fills, which is how a route goes missing on a style
                   that is drawing it perfectly well. */
                slot: ROUTE_SLOT,
                source: ROUTE_SOURCE_ID,
                layout: {'line-cap': 'round', 'line-join': 'round'},
                paint: {
                    'line-color': ROUTE_COLOR,
                    'line-width': 9,
                    'line-dasharray': [0, 1.6],
                    'line-emissive-strength': EMISSIVE,
                },
            })
            setReady(true)
        })

        cache.token = token
        cache.host = host
        cache.map = map

        return () => {
            /* ⚠️ DETACHED, NEVER `remove()`d. Destroying it here is what made every reopen
               a cold build: a new GL context, the style and its indoor config fetched again,
               and every tile of the terminal downloaded a second time. */
            if (host.parentNode) host.parentNode.removeChild(host)
            mapRef.current = null
            setReady(false)
        }
        /* ⚠️ `maxPitch` AND `initialCamera` ARE NOT DEPENDENCIES, and must not become them.
           The pitch ceiling is read here for the opening value and applied live by the effect
           below; the camera describes the map's birth and nothing else. Either one in the
           array would tear the map down and rebuild it — losing the style, the tiles and
           whatever the walker was looking at — the moment it changed, which for
           `initialCamera` is the instant their first GPS fix lands. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    /* The ceiling, applied live. See `MAPBOX_FLOOR` for why the floor moves first. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return
        map.setMinZoom(MAPBOX_FLOOR)
        map.setMaxZoom(WALK_MAX_ZOOM)
        map.setMinZoom(WALK_MIN_ZOOM)
        map.setMaxPitch(maxPitch)
    }, [ready, maxPitch])

    /**
     * ── LOOK AROUND ──────────────────────────────────────────────────────────────
     * Drag TURNS and TILTS the camera; it does not move it. Unconditional here, where it was
     * a prop on the shared map: a view out of a walker's eyes has no other mode.
     *
     * ⚠️ `dragPan` IS DISABLED RATHER THAN FOUGHT. Left running, both act on the same
     * gesture and the map spins and slides at once. It is also the half that keeps the camera
     * ON the walker — with panning off, a drag cannot take the view somewhere they are not.
     *
     * ⚠️ POINTER EVENTS, NOT MOUSE EVENTS, so one implementation covers a finger and a
     * mouse. `setPointerCapture` keeps a drag alive when the pointer leaves the canvas
     * mid-turn; without it a quick flick ends wherever the cursor crossed the edge.
     *
     * ⚠️ THE CAMERA FOLLOWS THE HAND, BOTH AXES. Drag right and the view swings right; drag
     * down and it tilts toward the floor. That is the opposite of dragging a MAP, where the
     * world follows the hand — but nothing here slides, and grabbing a camera to push it
     * right only for the view to swing left is the inversion people report as "backwards".
     */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return undefined

        map.dragPan.disable()

        const canvas = map.getCanvas()
        let dragging = false
        let last = null

        /* Degrees per pixel. Low enough that a deliberate look does not overshoot, high
           enough that a full turn does not need three drags across a phone screen. */
        const TURN = 0.25
        const TILT = 0.2

        const down = (e) => {
            if (e.button != null && e.button !== 0) return
            dragging = true
            last = {x: e.clientX, y: e.clientY}
            canvas.setPointerCapture?.(e.pointerId)
        }
        const move = (e) => {
            if (!dragging || !last) return
            const dx = e.clientX - last.x
            const dy = e.clientY - last.y
            last = {x: e.clientX, y: e.clientY}
            map.setBearing(map.getBearing() + dx * TURN)
            /* Clamped to the MAP's own ceiling rather than to a constant, so this can never
               silently disagree with `maxPitch`. */
            map.setPitch(Math.max(0, Math.min(map.getMaxPitch(), map.getPitch() - dy * TILT)))
        }
        const up = (e) => {
            dragging = false
            last = null
            canvas.releasePointerCapture?.(e.pointerId)
        }

        canvas.addEventListener('pointerdown', down)
        canvas.addEventListener('pointermove', move)
        canvas.addEventListener('pointerup', up)
        canvas.addEventListener('pointercancel', up)

        return () => {
            canvas.removeEventListener('pointerdown', down)
            canvas.removeEventListener('pointermove', move)
            canvas.removeEventListener('pointerup', up)
            canvas.removeEventListener('pointercancel', up)
            map.dragPan.enable()
        }
    }, [ready])

    /* ── The line ─────────────────────────────────────────────────────────────────
       ⚠️ `[lng, lat]`, THE REVERSE OF EVERYTHING ELSE IN THIS CODEBASE. Mapbox takes it that
       way round and fails silently when it does not get it: the walk is simply drawn in the
       wrong hemisphere, off the edge of a camera pinned to the walker, which looks exactly
       like a route that failed to solve. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return
        const source = map.getSource(ROUTE_SOURCE_ID)
        if (!source) return
        const coordinates = (pathPoints || [])
            .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng))
            .map((p) => [p.lng, p.lat])
        source.setData({type: 'Feature', geometry: {type: 'LineString', coordinates}})
    }, [ready, pathPoints])

    /* ── The two markers ──────────────────────────────────────────────────────────
       Torn down and rebuilt as a pair. There are two of them and one moves at walking pace;
       reconciling them individually would be more machinery than redrawing them is worth.

       ⚠️ NO ORIGIN PIN. On the shared map the walk passed its start point and got one, drawn
       underneath the walker's own disc — because on this screen the start IS the walker. Two
       markers on one coordinate is a rendering fault that happened to be hidden. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return undefined

        markersRef.current.forEach((m) => m.remove())
        markersRef.current = []

        /* THE WALKER, on a white disc so a thin line-art figure reads against a pale
           terminal floor. */
        if (Number.isFinite(passenger?.lat) && Number.isFinite(passenger?.lng)) {
            const el = document.createElement('div')
            el.style.cssText = 'width:34px;height:34px;border-radius:50%;background:#fff;'
                + 'box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;'
                + 'justify-content:center;pointer-events:none'
            el.innerHTML = personSvg
            const svg = el.querySelector('svg')
            if (svg) {
                svg.setAttribute('width', '20')
                svg.setAttribute('height', '20')
            }
            markersRef.current.push(
                new mapboxgl.Marker({element: el, anchor: 'center'})
                    .setLngLat([passenger.lng, passenger.lat]).addTo(map),
            )
        }

        /* THE BOOTH. `?raw` and injected as real DOM rather than used as an <img>: this
           glyph draws its glow through a `foreignObject`, which the browser's restricted
           image mode refuses to render — as an image it comes out all but blank. */
        if (Number.isFinite(destination?.lat) && Number.isFinite(destination?.lng)) {
            const el = document.createElement('div')
            el.style.cssText = 'width:32px;height:32px;display:flex;align-items:center;'
                + 'justify-content:center;overflow:visible;pointer-events:none'
            el.innerHTML = boothSvg
            const svg = el.querySelector('svg')
            if (svg) {
                svg.setAttribute('width', '32')
                svg.setAttribute('height', '32')
                svg.style.overflow = 'visible'
            }
            markersRef.current.push(
                new mapboxgl.Marker({element: el, anchor: 'center'})
                    .setLngLat([destination.lng, destination.lat]).addTo(map),
            )
        }

        return () => {
            markersRef.current.forEach((m) => m.remove())
            markersRef.current = []
        }
    }, [ready, passenger?.lat, passenger?.lng, destination?.lat, destination?.lng])

    /* ── The camera the host asked for ────────────────────────────────────────────
       ⚠️ NOTHING ELSE MOVES THIS CAMERA, which is what the shared map needed a hold flag to
       promise. There is no route framing here and no follow mode, so a request is not
       competing with anything — it simply is where the camera goes. */
    const lastFocusRef = useRef(null)
    useEffect(() => {
        const map = mapRef.current
        const id = focusRequest?.id
        if (!ready || !map || id == null || lastFocusRef.current === id) return
        lastFocusRef.current = id

        const {lat, lng, zoom, pitch, bearing, duration, offset} = focusRequest
        map.easeTo({
            ...(Number.isFinite(lat) && Number.isFinite(lng) ? {center: [lng, lat]} : {}),
            ...(Number.isFinite(zoom) ? {zoom} : {}),
            ...(Number.isFinite(pitch) ? {pitch} : {}),
            ...(Number.isFinite(bearing) ? {bearing} : {}),
            /* ⚠️ WHERE ON THE SCREEN THE WALKER LANDS, in pixels from the middle. A steeply
               pitched camera spends the top of the frame on ceiling and horizon, so a subject
               centred in the DATA sits high in the PICTURE with dead floor under it. `[0, n]`
               pushes it n pixels down. Screen space, not world space: it moves the camera,
               not the walker. */
            ...(Array.isArray(offset) ? {offset} : {}),
            duration: Number.isFinite(duration) ? duration : 900,
            /* Survives `prefers-reduced-motion`, which would otherwise cancel the move
               outright and leave the camera behind the walker for good. */
            essential: true,
        })
    }, [ready, focusRequest])

    /* ── Mapbox's own floor selector ──────────────────────────────────────────────
       Bottom-left: it puts itself top-right by default, which is the corner the sheet's close
       button already owns. It fills itself from the indoor data under the camera, so it shows
       nothing until the camera is actually over a mapped terminal.

       ⚠️ FEATURE-CHECKED, because `IndoorControl` is experimental: a minor version that drops
       or renames it should cost this screen a control, not the map. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map || !INDOOR_STYLE_CONFIG?.basemap?.showIndoor) return undefined
        if (typeof mapboxgl.IndoorControl !== 'function') return undefined

        const control = new mapboxgl.IndoorControl()
        map.addControl(control, 'bottom-left')
        return () => { if (map.hasControl?.(control)) map.removeControl(control) }
    }, [ready])

    /**
     * ── WHICH FLOOR IT OPENS ON ──────────────────────────────────────────────────
     * ⚠️ THE MANAGER IS REACHED THROUGH `map.style`, WHICH IS NOT PUBLIC API. There is no
     * documented way to set a floor: `IndoorControl` is experimental and declares no method
     * for it, and `Map` exposes nothing either. `style.indoorManager` is what the control
     * itself talks to — the same path, just not one Mapbox has promised to keep — so every
     * step is feature-checked and the whole thing is best-effort.
     *
     * ⚠️ AND IT WAITS FOR `selector-update`. The floor list does not exist at `load`; it is
     * built from indoor tiles once the camera is over a building, which is several frames
     * later and sometimes several seconds. Reading it eagerly finds nothing.
     *
     * ⚠️ ONCE. The event fires again on every tap of the selector, and the walker's own
     * choice of floor has to outlast ours.
     */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map || !defaultFloor) return undefined

        const manager = map.style?.indoorManager
        if (typeof manager?.on !== 'function' || typeof manager.selectFloor !== 'function') return undefined

        let applied = false
        const apply = (model) => {
            if (applied) return
            const floor = model?.floors?.find((f) => String(f.name) === String(defaultFloor))
            if (!floor) return
            applied = true
            if (model.selectedFloorId !== floor.id) manager.selectFloor(floor.id)
        }

        manager.on('selector-update', apply)
        // In case the list is already built — a re-mount over the same style, say.
        if (typeof manager.getControlState === 'function') {
            try { apply(manager.getControlState()) } catch { /* not ready yet; the event covers it */ }
        }
        return () => { manager.off?.('selector-update', apply) }
    }, [ready, defaultFloor])

    if (!token) {
        return (
            <div className={`flex w-full h-full items-center justify-center bg-(--bg-grey) ${className}`}>
                <p className="px-6 text-center text-sm text-(--text-secondary)">
                    {configFailed
                        ? 'Map unavailable — the app config could not be loaded.'
                        : 'Loading map…'}
                </p>
            </div>
        )
    }

    return <div ref={containerRef} className={`w-full h-full ${className}`}/>
}

export {WalkMap}
